"use client";

/**
 * Market-comment version of PostCommentFlagModal. Same UX shape
 * (centered modal on desktop, full-width bottom sheet on mobile,
 * Esc + click-outside, character counter, auto-close on success)
 * but calls api.flagComment instead of api.flagPostComment.
 *
 * Mirrors apps/web/components/posts/PostCommentFlagModal.tsx
 * byte-for-byte except the api method.
 *
 * 2026-05-06 — both modals copied intentionally to ship parity
 * faster. Filed as follow-up: extract a generic CommentReportModal
 * that takes onSubmit: (reason: string) => Promise<void> and have
 * both surfaces wrap it. Same dedup applies to the OverflowMenu
 * components.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Flag, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";

interface Props {
  open: boolean;
  onClose: () => void;
  commentId: string;
  // Optional contextual snippet rendered above the textarea so the
  // user can confirm WHICH comment they're flagging. Null → skip.
  commentExcerpt?: string | null;
}

const MIN_LEN = 1;
const MAX_LEN = 500;

export function MarketCommentFlagModal({
  open,
  onClose,
  commentId,
  commentExcerpt,
}: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReason("");
       
      setError(null);
       
      setDone(false);
      const id = window.setTimeout(() => textareaRef.current?.focus(), 30);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function handleSubmit() {
    const trimmed = reason.trim();
    if (trimmed.length < MIN_LEN || trimmed.length > MAX_LEN || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.flagComment(commentId, trimmed);
      setDone(true);
      window.setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t(
                "marketCommentFlag.errorFallback",
                "Couldn't submit report. Try again.",
              ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
      style={{
        background: "color-mix(in srgb, black 55%, transparent)",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="market-comment-flag-title"
    >
      <div
        className="w-full max-w-[480px] rounded-t-xl border bg-[var(--color-surface)] p-4 sm:rounded-xl"
        style={{
          borderColor: "var(--color-border)",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
        }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex h-7 w-7 items-center justify-center rounded-full"
              style={{
                background:
                  "color-mix(in srgb, var(--color-no) 12%, transparent)",
                color: "var(--color-no)",
              }}
            >
              <Flag size={14} strokeWidth={2.25} aria-hidden="true" />
            </span>
            <h2
              id="market-comment-flag-title"
              className="text-[14px] font-bold text-[var(--color-text)]"
            >
              {t(
                "marketCommentFlag.title",
                "Why are you flagging this comment?",
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("marketCommentFlag.close", "Close")}
            className="-mr-1 -mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-text3)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        {commentExcerpt && (
          <blockquote
            className="mb-3 rounded-md border-l-2 px-3 py-2 text-[12px] italic text-[var(--color-text2)]"
            style={{
              borderColor: "var(--color-border)",
              background: "var(--color-bg)",
            }}
          >
            {commentExcerpt.length > 180
              ? commentExcerpt.slice(0, 180) + "…"
              : commentExcerpt}
          </blockquote>
        )}

        <textarea
          ref={textareaRef}
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, MAX_LEN))}
          rows={4}
          maxLength={MAX_LEN}
          disabled={submitting || done}
          placeholder={t(
            "marketCommentFlag.placeholder",
            "e.g. spam, harassment, off-topic",
          )}
          className="w-full resize-none rounded-md border bg-[var(--color-bg)] p-2.5 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-brand)]"
          style={{ borderColor: "var(--color-border)" }}
        />

        <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-[var(--color-text3)]">
          <span>
            {reason.trim().length}/{MAX_LEN}
          </span>
          {error && (
            <span role="alert" style={{ color: "var(--color-no)" }}>
              {error}
            </span>
          )}
          {done && (
            <span role="status" style={{ color: "var(--color-yes)" }}>
              {t("marketCommentFlag.submitted", "Report submitted. Thanks.")}
            </span>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex min-h-[40px] items-center rounded-md border px-3 py-1.5 text-[12px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-text3)]"
            style={{ borderColor: "var(--color-border)" }}
          >
            {t("marketCommentFlag.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || done || reason.trim().length < MIN_LEN}
            className="inline-flex min-h-[40px] items-center rounded-md px-3 py-1.5 text-[12px] font-bold text-white transition-colors disabled:opacity-60"
            style={{ background: "var(--color-no)" }}
          >
            {submitting
              ? t("marketCommentFlag.submitting", "Submitting…")
              : t("marketCommentFlag.submit", "Submit flag")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
